import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Globe, Calendar, User, Package } from 'lucide-react';
import ConsignmentEntryFormI from './ConsignmentEntryFormI';
import apiClient from '../services/api';

const AMBER      = '#f59e0b';
const AMBER_DARK = '#d97706';

const ConsignmentI = () => {
  const [consignments, setConsignments] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [showForm,     setShowForm]     = useState(false);
  const [editData,     setEditData]     = useState(null);
  const [searchTerm,   setSearchTerm]   = useState('');

  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };
  const session = getSessionData();

  useEffect(() => { loadConsignments(); }, []);

  const loadConsignments = async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.get('/ConsignmentsI.php');
      if (res.data.success) setConsignments(res.data.data.consignments || []);
      else throw new Error(res.data.message || 'Failed to load');
    } catch (err) {
      let msg = 'Failed to load consignments';
      if (err.response?.status === 401) { msg = 'Session expired. Please login again.'; setTimeout(() => { window.location.href = '/login'; }, 2000); }
      else if (err.response) msg = err.response.data?.message || msg;
      else if (err.request)  msg = 'Server not responding. Please check if backend is running.';
      else                   msg = err.message;
      setError(msg);
    } finally { setLoading(false); }
  };

  const handleNew   = () => { setEditData(null); setShowForm(true); };
  const handleClose = () => { setShowForm(false); setEditData(null); };

  const handleEdit = async (c) => {
    try {
      const res = await apiClient.get(`/ConsignmentsI.php?awb=${c.ConsignmentNo}`);
      if (res.data.success) { setEditData(res.data.data); setShowForm(true); }
      else alert('Error loading consignment: ' + res.data.message);
    } catch (err) { alert('Error loading consignment: ' + (err.response?.data?.message || err.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this consignment?')) return;
    try {
      const res = await apiClient.delete(`/ConsignmentsI.php/${id}`);
      if (res.data.success) setConsignments(prev => prev.filter(c => c.ConsignmentID !== id));
      else alert('Error: ' + res.data.message);
    } catch (err) { alert('Error deleting consignment: ' + (err.response?.data?.message || err.message)); }
  };

  const handleSave = async (formData) => {
    try {
      const isEdit = formData.ConsignmentID;
      const res = isEdit
        ? await apiClient.put(`/ConsignmentsI.php/${formData.ConsignmentID}`, formData)
        : await apiClient.post('/ConsignmentsI.php', formData);
      if (res.data.success) {
        alert(isEdit ? `Consignment updated: ${formData.ConsignmentNo}` : `Consignment created: ${formData.ConsignmentNo}`);
        setShowForm(false); setEditData(null); loadConsignments();
      } else throw new Error(res.data.message);
    } catch (err) { throw new Error(err.response?.data?.message || err.message); }
  };

  if (!session?.companyid) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <Globe size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const filteredConsignments = consignments.filter(c =>
    c.ConsignmentNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ShipperName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ConsigneeName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusCls = (color) => {
    const map = { green: 'bg-emerald-100 text-emerald-700', success: 'bg-emerald-100 text-emerald-700', yellow: 'bg-amber-100 text-amber-700', warning: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700', danger: 'bg-red-100 text-red-700', blue: 'bg-blue-100 text-blue-700', info: 'bg-blue-100 text-blue-700' };
    return map[color] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* HEADER */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/25 flex items-center justify-center flex-shrink-0">
            <Globe size={26} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>International Consignments</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.85)' }}>Manage international shipments and exports</p>
          </div>
        </div>
        <button onClick={handleNew}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-amber-50"
          style={{ color: AMBER_DARK }}>
          <Plus size={17} /> New Consignment
        </button>
      </div>

      {/* TOOLBAR */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[280px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-amber-400 focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(245,158,11,0.12)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by consignment no, shipper, or consignee..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm font-medium text-gray-500 whitespace-nowrap">
          <span className="inline-block px-2.5 py-1 rounded-full text-[12px] font-bold text-white"
            style={{ background: AMBER }}>
            {filteredConsignments.length}
          </span>
          {filteredConsignments.length === 1 ? 'consignment' : 'consignments'}
        </div>
      </div>

      {/* BODY */}
      <div className="flex-1 p-5 overflow-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-gray-400">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-amber-400 rounded-full animate-spin" />
            <p className="text-sm">Loading consignments...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 gap-5">
            <div className="px-5 py-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-medium">
              <strong>Error:</strong> {error}
            </div>
            <button onClick={loadConsignments}
              className="px-4 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
              style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>
              Retry
            </button>
          </div>
        ) : filteredConsignments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center bg-white rounded-xl border border-gray-200">
            <Globe size={48} className="text-gray-300" />
            <h3 className="text-lg font-bold text-gray-600">No consignments found</h3>
            <p className="text-gray-400 text-sm max-w-[380px]">
              {searchTerm ? 'Try adjusting your search terms' : 'Get started by creating your first international consignment'}
            </p>
            {!searchTerm && (
              <button onClick={handleNew}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer mt-2"
                style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>
                <Plus size={16} /> New Consignment
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
              <table className="w-full border-collapse" style={{ minWidth: 900, tableLayout: 'fixed' }}>
                <thead className="sticky top-0 z-10">
                  <tr style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>
                    {[['Consignment No','14%'],['Date','9%'],['Shipper','14%'],['Consignee','14%'],['Country → Country','20%'],['Weight (kg)','9%'],['Status','10%'],['Actions','10%']].map(([h,w]) => (
                      <th key={h} style={{ width: w }}
                        className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-white border-r border-white/20 last:border-r-0">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredConsignments.map(c => (
                    <tr key={c.ConsignmentID}
                      className="border-b border-gray-100 transition-all duration-150 hover:bg-amber-50/40 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#f59e0b]">
                      <td className="px-4 py-3.5">
                        <span className="font-mono text-[12px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded">{c.ConsignmentNo}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5 text-gray-500 text-sm">
                          <Calendar size={13} className="text-gray-400 flex-shrink-0" />
                          {new Date(c.ConsignmentDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-start gap-1.5">
                          <User size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="font-medium text-gray-800 text-sm truncate">{c.ShipperName || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-start gap-1.5">
                          <User size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="font-medium text-gray-800 text-sm truncate">{c.ConsigneeName || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-[13px] text-gray-500 whitespace-nowrap">
                        {c.ShipperCountry || 'N/A'} → {c.ConsigneeCountry || 'N/A'}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5 font-mono font-semibold text-amber-700 text-sm">
                          <Package size={13} className="text-gray-400 flex-shrink-0" />
                          {c.Weight ? parseFloat(c.Weight).toFixed(2) : '0.00'}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${statusCls(c.StatusColor)}`}>
                          {c.Status || 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex gap-1.5">
                          <button onClick={() => handleEdit(c)} title="Edit"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border cursor-pointer border-blue-200 text-blue-600 bg-blue-50 transition-all duration-150 hover:bg-blue-600 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                            <Edit2 size={13} />
                          </button>
                          <button onClick={() => handleDelete(c.ConsignmentID)} title="Delete"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border cursor-pointer border-red-200 text-red-500 bg-red-50 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showForm && <ConsignmentEntryFormI onClose={handleClose} onSave={handleSave} editData={editData} />}
    </div>
  );
};

export default ConsignmentI;